import { sumBy } from 'lodash';
import { msToD } from '../../lib/time';
import { generateClientId, type ClientId } from '../core/ClientId';
import type { EventOrLeg, Events, TimeData } from '../core/TimeData';
import {
  isTimeData,
  isCargoEventTimeData,
  isPortCallEventTimeData,
  isLoadEventTimeData,
  isDischargeEventTimeData,
} from '../core/TimeData';
import type { EventTimeline } from '../core/Timelines';
import type { Distances } from '../distances/context';
import type { PnlData } from '../hooks/usePnlData';
import type { Cost } from './costs/costs';
import { getExtraCostsForLoadedAmount } from './costs/extraCost';
import type {
  GetIncomeForLoadedAmount,
  Income,
  IncomeParam,
} from './income/income';
import { getTDIncomeAndMassDistance } from './income/income';
import { getEventsFinancialBounds, getTimelineBounds } from './timelineBounds';
import type { LoadedAmount } from './loadedAmounts';
import { DefaultMap, mapGetFirstEntry } from '@seaber/common/lib/collections';
import { filteredMap, NONE } from '../../lib/util';
import { convertToDefaultCurrency } from '../../state/zustand';
import { getEventShipmentId } from './shipment';
import type { ScenarioOrder } from '../../lib/planningData';
import type { Pnl } from '../pnl/ui/context';

/**
 * @param income
 * @param days
 * @param costs Total costs, excluding tc costs
 */
export const calculateTce = (income: number, costs: number, days: number) =>
  days > 0 ? (income - costs) / days : 0;

interface VoyageTce {
  value: number;
  params: IncomeParam[];
}

export const getTimelineTce = (
  timeline: EventTimeline,
  costs: Cost[],
  income: Income[],
  euEtsCost: number,
  fuelEuCosts: number,
): VoyageTce | null => {
  const totalCosts =
    sumBy(costs, (cost) => cost.value ?? 0) + euEtsCost + fuelEuCosts;
  const totalIncome = sumBy(income, (income) => income.value ?? 0);
  const { startTime, endTime } = getTimelineBounds(timeline);

  if (!startTime || !endTime) {
    console.error(
      'getTimelineTce error: Start / end time not found for timeline',
      startTime,
      endTime,
    );

    return null;
  }

  const durationInDays = msToD(endTime - startTime);

  const tce = calculateTce(totalIncome, totalCosts, durationInDays);

  return {
    value: tce,
    params: [
      { name: 'Total income', value: totalIncome },
      { name: 'Total costs', value: totalCosts },
      { name: 'Duration (d)', value: durationInDays },
    ],
  };
};

export type TceComputationOptions = {
  firstIndex?: number;
  lastIndex?: number;
  euEtsCostEnabled: boolean;
  fuelEuCostEnabled: boolean;
  // TODO: outgoingCoaId seems to be  unused everywhere. remove or consider in implementation?
  outgoingCoaId: number | null;
  pnlData: PnlData | null;
  portCallCostSharingDetails: CostSharingDetailsByPortCall;
};

export type PortCallCostSharingDetails = {
  /** First loaded shipment of the currently carried ones */
  firstShipmentOnBoard: ClientId | null;
  /** All shipments that are carried/loaded/discharged during the port call */
  shipmentsOnBoard: Set<ClientId>;
  /** All shipments that have cargo operations during the port call */
  shipmentsWithCargoOperations: Set<ClientId>;
};

function createEmptyPortCallCostSharingDetails(): PortCallCostSharingDetails {
  return {
    firstShipmentOnBoard: null,
    shipmentsOnBoard: new Set(),
    shipmentsWithCargoOperations: new Set(),
  };
}

export type CostSharingDetailsByPortCall = Map<
  ClientId,
  PortCallCostSharingDetails
>;

export const MISSING_SHIPMENT_ID = generateClientId();

const loggedMissingShipmentIds = new Set<number>();
export function logMissingShipmentId(order: ScenarioOrder) {
  if (!loggedMissingShipmentIds.has(order.id)) {
    console.error('No shipment id for assigned order', order);
    loggedMissingShipmentIds.add(order.id);
  }
}

/**
 * Get cost sharing details for port calls from time data.
 *
 * NOTE: should be given all vessel time datas as input so
 * proper sharing details can be determined. Passing a partial
 * timeline can cause issues.
 */
export function getCostSharingDetailsForPortCalls(
  timeData: TimeData[],
): CostSharingDetailsByPortCall {
  const portCallDetailsByPortCallId: CostSharingDetailsByPortCall = new Map();

  let currentPortCallId = null;
  /** Tracks the order in which shipments are loaded (Map keys maintain their order) */
  const shipmentIdByLoadedOrder = new Map<number, ClientId>();

  let currentPortCallDetails: PortCallCostSharingDetails =
    createEmptyPortCallCostSharingDetails();

  for (const td of timeData) {
    const portCallId = td.event.portCallId;

    // Check if port call has changed
    if (portCallId && portCallId !== currentPortCallId) {
      // Save previous port call details
      if (currentPortCallId) {
        // Record all shipments that were carried during the port call and add potentially loaded/discharged ones
        const allShipments = new Set(shipmentIdByLoadedOrder.values());

        currentPortCallDetails.shipmentsWithCargoOperations.forEach(
          (shipmentId) => {
            allShipments.add(shipmentId);
          },
        );

        currentPortCallDetails.shipmentsOnBoard = allShipments;

        portCallDetailsByPortCallId.set(
          currentPortCallId,
          currentPortCallDetails,
        );
      }

      // Init new port call details
      currentPortCallId = portCallId;
      currentPortCallDetails = createEmptyPortCallCostSharingDetails();

      // If we arrive at a new port call with carried shipments, record which of them was loaded first
      if (shipmentIdByLoadedOrder.size > 0) {
        const firstEntry = mapGetFirstEntry(shipmentIdByLoadedOrder);
        if (firstEntry) {
          currentPortCallDetails.firstShipmentOnBoard = firstEntry[1];
        }
      }
    }

    if (isCargoEventTimeData(td)) {
      const shipmentId = getEventShipmentId(td.event) ?? MISSING_SHIPMENT_ID;

      // Shipment id is expected to be available for an order on the timeline
      if (shipmentId === MISSING_SHIPMENT_ID) {
        logMissingShipmentId(td.event.order);
      }

      if (isLoadEventTimeData(td)) {
        shipmentIdByLoadedOrder.set(td.event.order.id, shipmentId);

        // If we arrived to the port call without loaded shipments record this as the first one
        if (currentPortCallDetails.firstShipmentOnBoard === null) {
          currentPortCallDetails.firstShipmentOnBoard = shipmentId;
        }
      }

      if (isDischargeEventTimeData(td)) {
        if (shipmentIdByLoadedOrder.has(td.event.order.id)) {
          shipmentIdByLoadedOrder.delete(td.event.order.id);
        } else {
          console.error(
            'No matching load event found for order',
            td.event.order.id,
          );
        }
      }

      currentPortCallDetails.shipmentsWithCargoOperations.add(shipmentId);
    }
  }

  // Save the final port call details after loop completes
  if (currentPortCallId) {
    portCallDetailsByPortCallId.set(currentPortCallId, currentPortCallDetails);
  }

  return portCallDetailsByPortCallId;
}

export type TceResult = ReturnType<typeof computeTceForTimeData>;

/** Compute TCE for a given sequence of events, typically a single voyage/shipment. */
export function computeTceForTimeData(
  events: readonly EventOrLeg[],
  /** For order events, only count events for the given shipment (and null). When null, count all events. */
  shipmentId: ClientId | null,
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount,
  distances: Distances,
  options: TceComputationOptions,
  orderById: Map<number, ScenarioOrder> | null,
  pnl: Pnl,
): {
  income: number;
  voyageCost: number;
  tcCost: number;
  durationInDays: number;
  seaDurationInDays: number;
  portActivitiesInDays: number;
  earliestStart: number;
  latestEnd: number;
  tce: number;
  loadedAmounts: LoadedAmount[];
  loadedVoyageCharterIds: Set<number>;
  missingIncomeOrderIds: Set<number>;
  totalBunkerCost: number;
  totalChannelCost: number;
  totalExtraCosts: number;
  totalPortCost: number;
  totalVoyageCharterPortCost: number;
  totalEstimatedPortCost: number;
  totalCost: number;
  totalCO2: number;
} | null {
  const firstIndex = options.firstIndex ?? 0;
  const lastIndex = options.lastIndex ?? events.length - 1;

  const relevantEvents = events.slice(firstIndex, lastIndex + 1);
  const financialBounds = getEventsFinancialBounds(relevantEvents);

  const earliestStart =
    financialBounds.startTime ?? relevantEvents.at(0)?.start;
  const latestEnd = financialBounds.endTime ?? relevantEvents.at(-1)?.end;

  if (!earliestStart || !latestEnd) return null;

  const prevTd = events.at(firstIndex - 1);
  const firstTd = events[firstIndex];
  const lastTd = events[lastIndex];

  const durationInDays =
    financialBounds.startTime && financialBounds.endTime
      ? msToD(financialBounds.endTime - financialBounds.startTime)
      : msToD(lastTd.end - (prevTd?.end ?? firstTd.start));

  const timeData = relevantEvents.filter(
    (td): td is TimeData<Events> =>
      isTimeData(td) &&
      (!shipmentId ||
        !isCargoEventTimeData(td) ||
        getEventShipmentId(td.event) === shipmentId ||
        getEventShipmentId(td.event) === null),
  );

  const {
    totalCO2,
    totalBunkerCost,
    totalChannelCost,
    totalVoyageCharterPortCost,
    totalEstimatedPortCost,
  } = getTCECosts(
    timeData,
    shipmentId,
    earliestStart,
    latestEnd,
    options.pnlData,
    options.portCallCostSharingDetails,
  );

  const {
    income,
    tcCost,
    missingIncomeOrderIds,
    loadedAmounts,
    loadedVoyageCharterIds,
  } = getTDIncomeAndMassDistance(timeData, getIncomeForLoadedAmount, distances);

  const extraCosts = options.pnlData
    ? getExtraCostsForLoadedAmount(
        loadedAmounts,
        loadedVoyageCharterIds,
        options.pnlData.incomingVoyageChartersById,
        orderById,
      )
    : [];
  const totalExtraCosts = sumBy(extraCosts, (c) => c.value);

  // Calculate sea and port durations
  const seaDurationInDays = sumBy(timeData, (td) => td.hoursAtSea ?? 0) / 24;
  const portActivitiesInDays =
    sumBy(timeData, (td) => td.portActivities ?? 0) / 24;

  const euEtsCost = options.euEtsCostEnabled
    ? sumBy(
        pnl.costs.filter(
          (cost) =>
            cost.shipmentId === shipmentId && cost.category === 'eu_ets_cost',
        ),
        (euEtsCost) => euEtsCost.amount,
      )
    : 0;

  const fuelEuCost = options.fuelEuCostEnabled
    ? sumBy(
        pnl.costs.filter(
          (cost) =>
            cost.shipmentId === shipmentId && cost.category === 'fuel_eu_cost',
        ),
        (fuelEuCost) => fuelEuCost.amount,
      )
    : 0;

  const voyageCost =
    totalBunkerCost +
    totalChannelCost +
    totalExtraCosts +
    totalVoyageCharterPortCost +
    totalEstimatedPortCost +
    euEtsCost +
    fuelEuCost;
  const totalCost = voyageCost + tcCost;
  const tce = calculateTce(income, voyageCost, durationInDays);

  return {
    income,
    voyageCost,
    tcCost,
    durationInDays,
    seaDurationInDays,
    portActivitiesInDays,
    earliestStart,
    latestEnd,
    tce,
    loadedAmounts,
    loadedVoyageCharterIds,
    missingIncomeOrderIds: new Set(missingIncomeOrderIds),
    totalBunkerCost,
    totalChannelCost,
    totalExtraCosts,
    totalPortCost: totalEstimatedPortCost + totalVoyageCharterPortCost,
    totalVoyageCharterPortCost,
    totalEstimatedPortCost,
    totalCost,
    totalCO2,
  };
}

function getIncomingVoyageChartersByPortCall(timeData: TimeData[]) {
  const incomingVoyageChartersByPortCallId = new DefaultMap<
    ClientId,
    Set<number>
  >(() => new Set());
  const currentLoadedIncomingVoyageCharterIds: number[] = [];

  timeData.forEach((td) => {
    const incomingVoyageCharterId = isCargoEventTimeData(td)
      ? (td.event.order.incomingVoyageCharterRelation?.voyageCharterId ?? null)
      : null;

    const portCallId = td.event.portCallId;

    if (portCallId) {
      currentLoadedIncomingVoyageCharterIds.forEach((vcId) => {
        incomingVoyageChartersByPortCallId.update(portCallId, (s) =>
          new Set(s).add(vcId),
        );
      });
    }

    if (isLoadEventTimeData(td) && incomingVoyageCharterId !== null) {
      currentLoadedIncomingVoyageCharterIds.push(incomingVoyageCharterId);
    }

    if (isDischargeEventTimeData(td) && incomingVoyageCharterId !== null) {
      const targetIndex = currentLoadedIncomingVoyageCharterIds.findIndex(
        (id) => id === incomingVoyageCharterId,
      );
      if (targetIndex > -1) {
        currentLoadedIncomingVoyageCharterIds.splice(targetIndex, 1);
      }
    }
  });

  return incomingVoyageChartersByPortCallId;
}

function getTCECosts(
  timeData: TimeData[],
  /** When null, count all port calls */
  shipmentId: ClientId | null,
  earliestStart: number,
  latestEnd: number,
  pnlData: PnlData | null,
  portCallCostSharingDetails: CostSharingDetailsByPortCall,
) {
  let totalCO2 = 0;
  let totalBunkerCost = 0;
  let totalChannelCost = 0;

  const estimatedPortCostsByPortCall = new DefaultMap<ClientId, number>(
    () => 0,
  );

  timeData.forEach((td, index) => {
    const portCallId = td.event.portCallId;
    const costSharingDetails =
      portCallId !== null
        ? portCallCostSharingDetails.get(portCallId)
        : undefined;

    const isFirstShipmentAtPortCall =
      shipmentId !== null
        ? costSharingDetails?.firstShipmentOnBoard === shipmentId
        : false;

    const hasCargoOperationsAtPortCall =
      shipmentId !== null
        ? costSharingDetails?.shipmentsWithCargoOperations.has(shipmentId)
        : true;

    const ignoreLegCosts =
      !!shipmentId &&
      ((index === 0 && td.laden) ||
        (shipmentId !== null && !isFirstShipmentAtPortCall));
    const ignoreBunkerCosts = td.start < earliestStart || td.start >= latestEnd;

    const currentPortCall = isPortCallEventTimeData(td) ? td : null;

    /* Port calls with no cargo events have shipmentCount 0 */
    const portCallShipmentCount = Math.max(
      costSharingDetails?.shipmentsOnBoard.size ??
        currentPortCall?.event.shipmentCount ??
        1,
      1,
    );

    const { channelCost, bunkerCosts, estimatedPortCosts } =
      countTimeDataTceCosts(
        td,
        portCallShipmentCount,
        ignoreLegCosts,
        ignoreBunkerCosts,
      );

    // Include port costs only if shipment has cargo operations at port
    if (portCallId !== null && hasCargoOperationsAtPortCall) {
      estimatedPortCostsByPortCall.update(
        portCallId,
        (x) => (x ?? 0) + estimatedPortCosts,
      );
    }

    totalCO2 += td.totalCO2;
    totalBunkerCost += bunkerCosts;
    totalChannelCost += channelCost;
  });

  const { totalVoyageCharterPortCost, totalEstimatedPortCost } = countPortCosts(
    timeData,
    estimatedPortCostsByPortCall,
    pnlData,
  );

  return {
    totalCO2,
    totalBunkerCost,
    totalChannelCost,
    totalVoyageCharterPortCost,
    totalEstimatedPortCost,
  };
}

function countTimeDataTceCosts(
  td: TimeData,
  splitAmongCount: number,
  ignoreLegCosts: boolean,
  ignoreBunkerCosts: boolean,
) {
  const legBunkerCostCombined =
    (td.costs.bunkerGrade.insideEcaData?.voyageCost ?? 0) +
    (td.costs.bunkerGrade.outsideEcaData?.voyageCost ?? 0);
  const idleCost = ignoreBunkerCosts
    ? 0
    : (td.costs.bunkerGrade.extraConsumptionData?.voyageCost ?? 0);
  const channelCost = ignoreLegCosts ? 0 : td.costs.channelCost;
  const legBunkerCost = ignoreLegCosts ? 0 : legBunkerCostCombined;
  const portBunkerCost = ignoreBunkerCosts
    ? 0
    : (td.costs.bunkerGrade.portData?.voyageCost ?? 0);

  const portCallCosts = td.costs.portCostEstimate ?? 0;
  const splitCosts = portCallCosts / splitAmongCount;

  return {
    channelCost,
    bunkerCosts: legBunkerCost + portBunkerCost + idleCost,
    estimatedPortCosts: splitCosts,
  };
}

const getVoyageCharterPortCost = (
  pnlData: PnlData | null,
  voyageCharterId: number,
  locationId: number,
) => {
  const vcData = pnlData?.portCostByVcMaps.get(
    `${voyageCharterId}-${locationId}`,
  );

  if (!vcData) return null;

  return convertToDefaultCurrency(
    vcData.cost ?? vcData.estimatedCost ?? 0,
    vcData.currency,
  );
};

/** Counts total voyage charter and estimated port costs. When a port call has a cost from voyage charter, it overrides the estimate.
 * Sum of vc costs and estimated port costs is the total port cost. */
function countPortCosts(
  timeData: TimeData[],
  estimatedPortCostsByPortCall: Map<ClientId, number>,
  pnlData: PnlData | null,
) {
  let totalVoyageCharterPortCost = 0;
  let totalEstimatedPortCost = 0;

  const locationIdByPortCallId = new Map<ClientId, number>(
    filteredMap(timeData, (td) =>
      td.event.portCallId !== null
        ? [td.event.portCallId, td.event.locationId]
        : NONE,
    ),
  );

  const incomingVoyageChartersByPortCallId =
    getIncomingVoyageChartersByPortCall(timeData);

  estimatedPortCostsByPortCall.forEach((estimatedCost, portCallId) => {
    const locationId = locationIdByPortCallId.get(portCallId);
    let ignoreEstimate = false;

    if (locationId !== undefined) {
      const incomingVoyageCharterIds =
        incomingVoyageChartersByPortCallId.get(portCallId);
      incomingVoyageCharterIds?.forEach((vcId) => {
        const voyageCharterPortCost = getVoyageCharterPortCost(
          pnlData,
          vcId,
          locationId,
        );
        if (voyageCharterPortCost) {
          ignoreEstimate = true;
          totalVoyageCharterPortCost += voyageCharterPortCost;
        }
      });
    }

    if (!ignoreEstimate) {
      totalEstimatedPortCost += estimatedCost;
    }
  });
  return { totalVoyageCharterPortCost, totalEstimatedPortCost };
}
